import { toLongDate } from '@utils/Dated';
import getCurrentUserId from '../../../../ui-framework/helpers/getCurrentUserId';

export { getCurrentUserId };

export const getSearchPlaceholder = formatMessage =>
  formatMessage({ id: 'TYPE_TO_SEARCH', defaultMessage: 'Type to search' });

// Only strings and numbers can be humanized; objects would stringify to
// "[object Object]", which must never reach the UI.
export const humanizeIssueMetadataValue = value => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return '';
  }

  return (
    value
      .toString()
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, character => character.toUpperCase()) || ''
  );
};

export const getLookupLabel = lookup => {
  if (typeof lookup === 'string') {
    return humanizeIssueMetadataValue(lookup) || lookup;
  }

  return lookup?.label || lookup?.code || '-';
};

export const getLookupCode = lookup =>
  typeof lookup === 'string' ? lookup : lookup?.code || '';

const getLookupLocaleId = (category, code, suffix = '') =>
  ['ISSUES', category, code, suffix]
    .filter(Boolean)
    .join('_')
    .replace(/[^A-Z0-9_]/gi, '_')
    .toUpperCase();

export const getLocalizedLookupLabel = (lookup, category, formatMessage) => {
  const fallback = getLookupLabel(lookup);
  const code = getLookupCode(lookup);

  if (!formatMessage || !category || !code || fallback === '-') {
    return fallback;
  }

  return formatMessage({
    id: getLookupLocaleId(category, code),
    defaultMessage: fallback,
  });
};

export const getLocalizedLookupDescription = (
  lookup,
  category,
  formatMessage,
) => {
  const fallback = lookup?.description || '';
  const code = getLookupCode(lookup);

  if (!fallback || !formatMessage || !category || !code) {
    return fallback;
  }

  return formatMessage({
    id: getLookupLocaleId(category, code, 'DESCRIPTION'),
    defaultMessage: fallback,
  });
};

// Never render a raw status code (e.g. "IN_PROGRESS"): prefer a localized label
// (ISSUES_STATUS_<CODE>) and fall back to the humanized English name.
export const humanizeIssueStatusCode = (value, formatMessage) => {
  if (value === undefined || value === null || value === '' || value === '-') {
    return '-';
  }

  const humanized = humanizeIssueMetadataValue(value);
  if (!humanized) {
    return '-';
  }

  if (!formatMessage) {
    return humanized;
  }

  const localeId = `ISSUES_STATUS_${value
    .toString()
    .trim()
    .replace(/[\s-]+/g, '_')
    .toUpperCase()}`;

  return formatMessage({ id: localeId, defaultMessage: humanized });
};

// Attaches each field's permission (from the permissions/config map) to the
// schema and drops fields the backend marks HIDDEN. Framework form fields honor
// the resulting `permission` prop (e.g. READ renders read-only). Fields outside
// the backend map (e.g. the admin-only company selectors) default to WRITE so
// they keep working when the form enforces per-field permissions.
export const applyIssueFieldPermissions = (
  schema,
  fieldPermissions = {},
  isHidden = () => false,
) =>
  (schema || [])
    .map(field => ({
      ...field,
      permission: fieldPermissions?.[field.name] ?? 'WRITE',
    }))
    .filter(field => !isHidden(field.permission));

export const getErrorMessage = (result, formatMessage) => {
  const first = result?.errors?.[0];
  return (
    first?.message ||
    result?.message ||
    (formatMessage
      ? formatMessage({
          id: 'UNABLE_TO_LOAD_REQUEST_DATA',
          defaultMessage: 'Unable to load request data',
        })
      : 'Unable to load request data')
  );
};

export const getUnavailableIssueMessage = formatMessage =>
  formatMessage
    ? formatMessage({
        id: 'ISSUE_UNAVAILABLE',
        defaultMessage: 'This request is unavailable or no longer exists.',
      })
    : 'This request is unavailable or no longer exists.';

export const formatIssueDateTime = value => (value ? toLongDate(value) : '-');

export const getIssueData = result => result?.data?.data || result?.data;

export const getResultItems = result => {
  if (!result?.ok) {
    return [];
  }
  return result.data?.data || [];
};

export const getActivityItems = result => {
  if (!result?.ok) {
    return [];
  }

  if (Array.isArray(result.data?.data)) {
    return result.data.data;
  }

  if (Array.isArray(result.data)) {
    return result.data;
  }

  return [];
};

export const getResultTotal = resultData =>
  resultData?.total ?? resultData?.totalCount ?? 0;

export const getActivityTotalPages = (result, fallbackLimit = 25) => {
  const resultData = result?.data || {};
  if (resultData.totalPages !== undefined && resultData.totalPages !== null) {
    return resultData.totalPages;
  }

  const total = resultData.totalCount ?? resultData.total ?? 0;
  const limit = resultData.limit || fallbackLimit;
  return limit ? Math.ceil(total / limit) : 0;
};

export const getLookupOptions = (lookups, category, formatMessage) =>
  (lookups || []).map(lookup => ({
    label: getLocalizedLookupLabel(lookup, category, formatMessage),
    value: lookup.code,
    tooltip: getLocalizedLookupDescription(lookup, category, formatMessage),
  }));

export const getReporterDisplayLabel = user => {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  return (
    name || user?.displayName || user?.label || user?.userId || user?.id || ''
  );
};

// Company types share the locale keys already used by the Reporter Type
// filter options (SERVICE_ADMINISTRATOR / SERVICE_PROVIDER / ADMIN).
export const humanizeCompanyType = (value, formatMessage) => {
  const humanized = humanizeIssueMetadataValue(value);
  if (!formatMessage || !humanized) {
    return humanized;
  }

  return formatMessage({
    id: `${value}`.toUpperCase(),
    defaultMessage: humanized,
  });
};

export const getIssueUserDisplayLabel = user => {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  return name || user?.displayName || user?.email || user?.label || '';
};

export const getIssueUserOptionLabel = user => {
  const displayName = getIssueUserDisplayLabel(user);
  return user?.email && user.email !== displayName
    ? `${displayName} (${user.email})`
    : displayName;
};

export const pinCurrentUserOption = (users, currentUser) => {
  const currentUserId = getCurrentUserId(currentUser);
  if (!currentUserId) {
    return users;
  }

  const normalizedCurrentUserId = currentUserId.toLowerCase();
  const isCurrentUser = user =>
    user?.id?.toLowerCase() === normalizedCurrentUserId;
  const directoryUser = users.find(isCurrentUser);
  const pinnedUser = {
    ...currentUser,
    ...directoryUser,
    id: currentUserId,
    isCurrentUser: true,
  };

  return [pinnedUser, ...users.filter(user => !isCurrentUser(user))];
};

const ASSIGNEE_ROLE_LABELS = {
  ADMIN: 'Service Xcelerator',
  SERVICE_ADMINISTRATOR: 'Service Administrator',
};

export const getAssigneeOrganizationLabel = issue => {
  if (issue?.assigneeCompanyDisplayName) {
    return issue.assigneeCompanyDisplayName;
  }

  if (issue?.assigneeRole === 'ADMIN') {
    return ASSIGNEE_ROLE_LABELS.ADMIN;
  }

  return (
    issue?.serviceAdministrator?.displayName ||
    issue?.serviceAdministrator?.companyName ||
    ASSIGNEE_ROLE_LABELS[issue?.assigneeRole] ||
    humanizeIssueMetadataValue(issue?.assigneeRole)
  );
};

export const getAssignedToDisplay = issue => ({
  organization: getAssigneeOrganizationLabel(issue),
  user: issue?.assigneeDisplayName || getIssueUserDisplayLabel(issue?.assignee),
});

export const getReporterContextLabel = (reporter, formatMessage) =>
  [
    getReporterDisplayLabel(reporter),
    humanizeIssueMetadataValue(reporter?.roleIdentifier),
    humanizeCompanyType(reporter?.companyType, formatMessage),
  ]
    .filter(Boolean)
    .join(' · ') || '-';

export const getCompanyNameLabel = (company, fallback = '-') =>
  company?.displayName ||
  company?.companyName ||
  company?.companyNumberInternal ||
  company?.companyNumber ||
  fallback;

export const getCompanyDisplayLabel = (company, formatMessage) =>
  [
    getCompanyNameLabel(company),
    humanizeCompanyType(company?.companyType, formatMessage),
  ]
    .filter(value => value && value !== '-')
    .join(' · ') || '-';

export const getProviderName = () => 'issuesTracking';

export const normalizeIssueForForm = issue => ({
  issueNumber: issue?.issueNumber || '',
  title: issue?.title || '',
  description: issue?.description || '',
  typeCode: issue?.type?.code || '',
  priorityCode: issue?.priority?.code || '',
  componentCode: issue?.component?.code || '',
});

export const normalizeIssueForStatusForm = issue => ({
  statusCode: issue?.status?.code || '',
  resolutionCode: getLookupCode(issue?.resolution || issue?.resolutionCode),
  resolutionNote: issue?.resolutionNote || '',
});

export const stripEmptyValues = data =>
  Object.fromEntries(
    Object.entries(data).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  );

export const filtersToApiParams = filters =>
  (filters || []).reduce((params, filter) => {
    if (!filter?.field || filter?.value === undefined || filter?.value === '') {
      return params;
    }

    const operator = filter?.value?.operator || filter?.operator;
    const value = filter?.value?.value || filter?.value;
    return {
      ...params,
      [filter.searchAs || filter.field]: operator
        ? `$${operator}:${value}`
        : value,
    };
  }, {});

export const getActivityChanges = activity => {
  const changes =
    activity?.payloadJson?.changes ||
    activity?.payloadJson?.auditData ||
    activity?.auditData;
  if (Array.isArray(changes) && changes.length > 0) {
    // Legacy audit rows can contain no-op entries (old and new both empty).
    return changes.filter(
      change =>
        change?.field &&
        ((change.oldValue !== null &&
          change.oldValue !== undefined &&
          change.oldValue !== '') ||
          (change.newValue !== null &&
            change.newValue !== undefined &&
            change.newValue !== '')),
    );
  }

  if (activity?.oldValue !== undefined || activity?.newValue !== undefined) {
    return [
      {
        field: ['CREATED', 'STATUS_CHANGED'].includes(activity?.activityType)
          ? 'statusCode'
          : 'value',
        oldValue: activity?.oldValue ?? null,
        newValue: activity?.newValue ?? null,
        oldDisplayValue: activity?.oldDisplayValue ?? null,
        newDisplayValue: activity?.newDisplayValue ?? null,
      },
    ].filter(change => change.oldValue !== null || change.newValue !== null);
  }

  return [];
};

export const humanizeIssueFieldName = (value, formatMessage) => {
  if (!value) {
    return formatMessage
      ? formatMessage({ id: 'FIELD', defaultMessage: 'Field' })
      : 'Field';
  }

  const humanized = value
    .replace(/Code$/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, character => character.toUpperCase());

  if (!formatMessage) {
    return humanized;
  }

  // Locale key per field (e.g. dueAt -> DUE_AT); falls back to the humanized
  // English name for fields without a translation.
  const localeId = value
    .replace(/Code$/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toUpperCase();
  return formatMessage({ id: localeId, defaultMessage: humanized });
};

export const humanizeActivityType = (value, formatMessage) => {
  if (!value) {
    return formatMessage
      ? formatMessage({ id: 'ACTIVITY', defaultMessage: 'Activity' })
      : 'Activity';
  }

  const humanized = value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^./, character => character.toUpperCase());

  if (!formatMessage) {
    return humanized;
  }

  return formatMessage({
    id: `ISSUES_ACTIVITY_TYPE_${value}`,
    defaultMessage: humanized,
  });
};

export const formatActivityValue = value => {
  if (value === undefined || value === null || value === '') {
    return '-';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return `${value}`;
};

// Lookup category per audited field, so old/new values can be localized the
// same way as the corresponding dropdown (falls back to the backend display
// value for non-lookup fields like title).
const ACTIVITY_FIELD_LOOKUP_CATEGORIES = {
  status: 'STATUS',
  type: 'TYPE',
  priority: 'PRIORITY',
  component: 'COMPONENT',
  resolution: 'RESOLUTION_CODE',
  source: 'SOURCE',
};

export const formatActivityDisplayValue = (change, key, formatMessage) => {
  const displayKey = key === 'oldValue' ? 'oldDisplayValue' : 'newDisplayValue';
  const displayValue = change?.[displayKey];
  const fallback = formatActivityValue(
    displayValue !== undefined && displayValue !== null && displayValue !== ''
      ? displayValue
      : change?.[key],
  );

  const code = change?.[key];

  // Assignee roles are company-type codes; localize them the same way.
  if (
    change?.field === 'assigneeRole' &&
    formatMessage &&
    typeof code === 'string' &&
    code
  ) {
    return humanizeCompanyType(code, formatMessage) || fallback;
  }

  const category =
    ACTIVITY_FIELD_LOOKUP_CATEGORIES[
      `${change?.field || ''}`.replace(/Code$/, '')
    ];
  if (!formatMessage || !category || typeof code !== 'string' || !code) {
    return fallback;
  }

  return getLocalizedLookupLabel(
    { code, label: fallback },
    category,
    formatMessage,
  );
};
